import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@dashboard/hooks/useAuth';
import { supabase } from '@shared/lib/supabase';
import { api } from '@shared/lib/api';

export default function StripeOnboardingRefresh() {
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [fetchingMerchant, setFetchingMerchant] = useState(true);
  const [hoveredLink, setHoveredLink] = useState(false);

  useEffect(() => {
    if (!user) {
      setFetchingMerchant(false);
      return;
    }
    supabase
      .from('merchants')
      .select('api_key')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setApiKey(data.api_key);
        setFetchingMerchant(false);
      });
  }, [user]);

  const handleRestart = async () => {
    if (!user || !apiKey) return;
    setLoading(true);
    setError('');

    try {
      const result = await api.stripeGetOnboardingUrl(apiKey);
      window.location.href = result.onboarding_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate onboarding link');
      setLoading(false);
    }
  };

  if (authLoading || fetchingMerchant) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dash-bg, #09090b)' }}>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--dash-bg, #09090b)' }}>
        <div className="w-full max-w-md text-center">
          <Link
            to="/"
            className="font-mono tracking-widest text-sm font-medium uppercase"
            style={{ color: 'var(--dash-text-primary)' }}
          >
            BONESPAY
          </Link>
          <div
            className="mt-10 rounded p-8 md:p-10"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
          >
            <h2
              className="font-serif text-2xl mb-3"
              style={{ color: 'var(--dash-text-primary)' }}
            >
              Sign in required
            </h2>
            <p
              className="font-sans text-sm leading-relaxed mb-6"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              You need to be signed in to restart Stripe onboarding.
            </p>
            <Link
              to="/login"
              className="inline-block bg-blue-600 text-white font-mono text-sm font-medium px-8 py-3 rounded hover:bg-blue-500 transition-colors uppercase tracking-wider"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--dash-bg, #09090b)' }}>
      <div className="w-full max-w-md text-center">
        <Link
          to="/"
          className="font-mono tracking-widest text-sm font-medium uppercase"
          style={{ color: 'var(--dash-text-primary)' }}
        >
          BONESPAY
        </Link>
        <div
          className="mt-10 rounded p-8 md:p-10"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: 'var(--dash-hover-bg)' }}
          >
            <span style={{ color: 'var(--dash-text-muted)' }} className="text-2xl">&#8635;</span>
          </div>
          <h2
            className="font-serif text-2xl mb-3"
            style={{ color: 'var(--dash-text-primary)' }}
          >
            Link Expired
          </h2>
          <p
            className="font-sans text-sm leading-relaxed mb-6"
            style={{ color: 'var(--dash-text-muted)' }}
          >
            Your Stripe onboarding link has expired. Click below to generate a new one
            and continue setting up your account.
          </p>

          {error && (
            <div className="border border-red-500/30 bg-red-500/10 rounded px-4 py-3 mb-4">
              <p className="font-sans text-sm text-red-300">{error}</p>
            </div>
          )}

          <button
            onClick={handleRestart}
            disabled={loading || !apiKey}
            className="w-full bg-blue-600 text-white font-mono text-sm font-medium py-3.5 rounded hover:bg-blue-500 transition-colors disabled:opacity-50 uppercase tracking-wider flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <span className="inline-block animate-spin">&#8635;</span>
                Generating link...
              </>
            ) : (
              'Restart Onboarding'
            )}
          </button>

          <div className="pt-6 mt-6" style={{ borderTop: '1px solid var(--dash-border)' }}>
            <Link
              to="/dashboard"
              className="font-mono text-xs transition-colors uppercase tracking-widest"
              style={{ color: hoveredLink ? 'var(--dash-text-muted)' : 'var(--dash-text-faint)' }}
              onMouseEnter={() => setHoveredLink(true)}
              onMouseLeave={() => setHoveredLink(false)}
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
